import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import {
  authTokenService,
  isPlatformAdminClaims,
  LocalJwtClaims,
  SESSION_EXPIRED,
  TOKEN_CLEARED,
} from '../services/auth.service';
import { getAdminAppBaseUrl } from '../utils/adminAppUrl';

// ── Types ────────────────────────────────────────────────────────────────────

interface AuthUser {
  id: string;
  email: string;
  role: string | null;
  customRoleId: string | null;
  createdById: string | null;
  permissions: Record<string, boolean>;
  tenantProfileId: string | null;
  accountStatus: string;
  mustChangePassword: boolean;
  mustVerifyPhone: boolean;
  displayName?: string | null;
  phone?: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    phoneOrOptions?:
      | string
      | {
          phone?: string;
          accountType?: 'LANDLORD' | 'MANAGEMENT_FIRM';
          country?: string;
          displayName?: string;
        },
  ) => Promise<void>;
  signOut: () => Promise<void>;
  getAuthToken: () => string | null;
  refreshUser: () => Promise<void>;
  /** Re-read JWT from storage into context (after Google sign-in on login page). */
  establishSessionFromToken: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Helpers ──────────────────────────────────────────────────────────────────

function getFriendlyAuthMessage(error: any): string {
  const msg = (error?.message ?? '') as string;
  if (error?.code === 'EMAIL_ALREADY_EXISTS')
    return 'An account with this email already exists. Please sign in instead.';
  if (error?.code === 'PHONE_ALREADY_EXISTS')
    return 'An account with this phone number already exists. Please use another phone number.';
  if (error?.code === 'INVALID_PHONE')
    return 'Enter a valid phone number in international format (for example, +2547XXXXXXXX).';
  if (error?.code === 'PHONE_REQUIRED')
    return 'Phone number is required for account verification.';
  if (error?.code === 'EMAIL_REGISTERED_AS_TENANT')
    return 'This email belongs to a tenant account. Please use the tenant portal.';
  if (error?.code === 'USER_NOT_FOUND')
    return 'No account found with this email. Please sign up first.';
  if (error?.code === 'INVALID_CREDENTIALS')
    return 'Invalid email or password.';
  if (error?.code === 'ACCOUNT_SUSPENDED')
    return 'Your account has been suspended. Please contact support.';
  if (error?.code === 'ACCOUNT_PENDING_APPROVAL')
    return 'Your signup is pending approval. We will call you shortly.';
  if (error?.code === 'PASSWORD_NOT_SET')
    return 'Your account was created via invitation. Please use "Forgot Password" to set your password.';
  if (error?.code === 'INVITATION_PENDING')
    return 'Please accept your team invitation from your email before signing in.';
  if (msg && !msg.includes('localhost') && !msg.includes('tenant-plus-backend'))
    return msg;
  return 'Something went wrong. Please try again.';
}

function userFromClaims(claims: LocalJwtClaims): AuthUser {
  return {
    id: claims.sub,
    email: claims.email,
    role: claims.role,
    customRoleId: claims.customRoleId,
    createdById: claims.createdById,
    permissions: claims.permissions,
    tenantProfileId: claims.tenantProfileId || null,
    accountStatus: claims.accountStatus,
    mustChangePassword: claims.mustChangePassword,
    mustVerifyPhone: claims.mustVerifyPhone,
  };
}

// ── Provider ──────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const initializedRef = useRef(false);

  // Restore landlord/tenant JWT only. Platform tokens on this host are leftovers
  // (admin SPA is a different origin) and must not hijack /login → admin bounce.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    authTokenService.clearPlatformToken();

    // Don't apply landlord auth on tenant routes
    if (location.pathname.startsWith('/tenant')) {
      setLoading(false);
      return;
    }

    const claims = authTokenService.getAppClaims();
    if (claims) {
      setUser(userFromClaims(claims));
    }
    setLoading(false);
  }, []);

  // Handle session expiry fired by authTokenService
  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      authTokenService.clearToken();
      // Use window.location for the live pathname — not a stale closure value.
      const { pathname } = window.location;
      if (!pathname.startsWith('/tenant') && !pathname.startsWith('/login')) {
        navigate('/login');
      }
    };
    window.addEventListener(SESSION_EXPIRED, handleExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, handleExpired);
  }, [navigate]); // location.pathname removed — use window.location inside handler

  // Clear user immediately when the JWT is explicitly wiped (e.g. TenantAuthContext.signOut)
  useEffect(() => {
    const handleCleared = () => {
      setUser(null);
    };
    window.addEventListener(TOKEN_CLEARED, handleCleared);
    return () => window.removeEventListener(TOKEN_CLEARED, handleCleared);
  }, []);

  // ── Auth actions ──────────────────────────────────────────────────────────

  const signInWithEmail = useCallback(async (email: string, password: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      const err: any = new Error(data.error || 'Login failed');
      err.code = data.code;
      const finalErr: any = new Error(getFriendlyAuthMessage(err));
      finalErr.code = data.code; // Pass the code up to the component
      throw finalErr;
    }

    const claims = authTokenService.setToken(data.token);
    if (!claims) throw new Error('Received an invalid token. Please try again.');
    if (isPlatformAdminClaims(claims)) {
      authTokenService.clearToken();
      const adminUrl = getAdminAppBaseUrl() || 'https://admin.tenantplus.co.ke';
      const err: any = new Error(
        `Platform admin accounts sign in at ${adminUrl} — not the landlord app.`
      );
      err.code = 'USE_PLATFORM_ADMIN_HOST';
      throw err;
    }
    setUser(userFromClaims(claims));
  }, []);

  const signUpWithEmail = useCallback(
    async (
      email: string,
      password: string,
      phoneOrOptions?:
        | string
        | {
            phone?: string;
            accountType?: 'LANDLORD' | 'MANAGEMENT_FIRM';
            country?: string;
            displayName?: string;
          },
    ): Promise<void> => {
      const opts =
        typeof phoneOrOptions === 'string' ? { phone: phoneOrOptions } : (phoneOrOptions || {});
    const res = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        phone: opts.phone,
        accountType: opts.accountType,
        country: opts.country,
        displayName: opts.displayName,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      const err: any = new Error(data.error || 'Registration failed');
      err.code = data.code;
      const finalErr: any = new Error(getFriendlyAuthMessage(err));
      finalErr.code = data.code; // Pass the code up to the component
      throw finalErr;
    }

    const claims = authTokenService.setToken(data.token);
    if (!claims) throw new Error('Received an invalid token. Please try again.');
    setUser(userFromClaims(claims));
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    authTokenService.clearToken();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const getAuthToken = useCallback((): string | null => {
    return authTokenService.getAppToken();
  }, []);

  /** Re-fetch user from me endpoint and update context. */
  const refreshUser = useCallback(async (): Promise<void> => {
    const header = authTokenService.getAppAuthHeader();
    if (!header) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: header },
      });
      if (res.ok) {
        const { user: freshUser } = await res.json();
        setUser(freshUser as AuthUser);
      }
    } catch { /* non-fatal */ }
  }, []);

  const establishSessionFromToken = useCallback((): void => {
    authTokenService.clearPlatformToken();
    const claims = authTokenService.getAppClaims();
    if (claims) {
      setUser(userFromClaims(claims));
      return;
    }
    // Google / password path may have just stored a platform JWT — refuse on this host
    const anyClaims = authTokenService.getClaims();
    if (isPlatformAdminClaims(anyClaims)) {
      authTokenService.clearToken();
      setUser(null);
    }
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    getAuthToken,
    refreshUser,
    establishSessionFromToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export default AuthContext;
